import type { IconKind } from '../components/Icon/Icon';
import type {
  TriageChannelConfig,
  TriageGoalTypeConfig,
  TriageGuidanceSourceConfig,
  TriagePriorityConfig,
  TriageSentimentConfig,
  TriageSourceTypeConfig,
  TriageStatusConfig,
  TriageTriggeredByConfig,
} from './types';

export const TI_CHANNELS: Record<string, TriageChannelConfig> = {
  'Phone Call': {
    icon: 'phone',
    label: 'Phone Call',
    shortLabel: 'Phone',
    color: 'var(--success)',
    bg: 'color-mix(in srgb, var(--success) 7%, transparent)',
  },
  SMS: {
    icon: 'chat',
    label: 'SMS',
    shortLabel: 'SMS',
    color: 'color-mix(in srgb, var(--gold) 78%, #000)',
    bg: 'color-mix(in srgb, var(--gold) 18%, var(--cream-1))',
  },
  Email: {
    icon: 'mail',
    label: 'Email',
    shortLabel: 'Email',
    color: 'var(--ink-1)',
    bg: 'var(--surface-mute)',
  },
  'Website Chat': {
    icon: 'globe',
    label: 'Website Chat',
    shortLabel: 'Chat',
    color: 'var(--gold)',
    bg: 'var(--gold-tint)',
  },
  Survey: {
    icon: 'clipboard',
    label: 'Survey',
    shortLabel: 'Survey',
    color: 'color-mix(in srgb, var(--success) 80%, #000)',
    bg: 'color-mix(in srgb, var(--success) 7%, transparent)',
  },
};

export const TI_PRIORITIES: Record<string, TriagePriorityConfig> = {
  urgent: {
    label: 'Urgent',
    pillClass: 'v6-pill-negative',
    textColor: 'var(--terracotta)',
  },
  high: {
    label: 'High',
    pillClass: 'v6-pill-warning',
    textColor: 'var(--gold)',
  },
  medium: {
    label: 'Medium',
    pillClass: 'v6-pill-brass',
    textColor: 'color-mix(in srgb, var(--gold) 78%, #000)',
  },
};

export const TI_STATUSES: Record<string, TriageStatusConfig> = {
  open: {
    key: 'open',
    label: 'Open',
    shortLabel: 'Open',
    color: 'var(--gold)',
    bg: 'var(--gold-tint)',
    dot: 'var(--gold)',
    description: 'Active — needs attention or in progress',
    canTransitionTo: ['waiting', 'closed'],
  },
  waiting: {
    key: 'waiting',
    label: 'Waiting',
    shortLabel: 'Waiting',
    color: 'color-mix(in srgb, var(--gold) 78%, #000)',
    bg: 'color-mix(in srgb, var(--gold) 18%, var(--cream-1))',
    dot: 'var(--gold)',
    description: 'Blocked — waiting on patient or external party',
    canTransitionTo: ['open', 'closed'],
  },
  closed: {
    key: 'closed',
    label: 'Closed',
    shortLabel: 'Closed',
    color: 'var(--ink-2)',
    bg: 'var(--surface-mute)',
    dot: 'var(--ink-3)',
    description: 'Archived — read-only',
    canTransitionTo: ['open'],
  },
};

export const TI_TRIGGERED_BY: Record<string, TriageTriggeredByConfig> = {
  recall: {
    label: 'Recall',
    color: 'var(--ai-3)',
    bg: 'var(--ai-tint)',
    icon: 'refresh',
  },
  rcm: {
    label: 'RCM',
    color: 'color-mix(in srgb, var(--gold) 78%, #000)',
    bg: 'color-mix(in srgb, var(--gold) 18%, var(--cream-1))',
    icon: 'doc',
  },
  daily_insight: {
    label: 'Daily insight',
    color: 'var(--success)',
    bg: 'color-mix(in srgb, var(--success) 7%, transparent)',
    icon: 'activity',
  },
};

export const TI_GOAL_TYPES: Record<string, TriageGoalTypeConfig> = {
  engagement: {
    label: 'Engagement',
    cannedNotes: [
      'Called — no answer.',
      'Called — left a voicemail.',
      'Spoke with patient — wished them a happy birthday.',
      'Spoke with patient — booked a hygiene cleaning.',
    ],
  },
  reactivate: {
    label: 'Reactivate patient',
    cannedNotes: [
      'Called — no answer.',
      'Called — left a voicemail.',
      'Spoke — patient booked a visit.',
      'Spoke — patient will think about it.',
      'Spoke — patient not interested right now.',
    ],
  },
  book: {
    label: 'Book appointment',
    cannedNotes: [
      'Called — no answer.',
      'Called — left a voicemail.',
      'Spoke — booked an appointment.',
      'Spoke — patient asked to be called back later.',
      'Spoke — patient not interested.',
    ],
  },
  clinical: {
    label: 'Clinical safety',
    cannedNotes: [
      'Called — no answer.',
      'Called — left a voicemail.',
      "Spoke — patient confirmed they're recovering well.",
      'Spoke — scheduled a follow-up visit.',
      'Spoke — escalated to the doctor.',
    ],
  },
  collect: {
    label: 'Collect payment',
    cannedNotes: [
      'Called — no answer.',
      'Called — left a voicemail.',
      'Spoke — payment received.',
      'Spoke — set up a payment plan.',
      'Spoke — patient disputed the charge.',
    ],
  },
};

export const TI_SOURCE_TYPES: Record<string, TriageSourceTypeConfig> = {
  call: { icon: 'phone', label: 'Open call transcript' },
  sms: { icon: 'chat', label: 'Open SMS thread' },
  email: { icon: 'mail', label: 'Open email thread' },
  survey: { icon: 'clipboard', label: 'Open survey response' },
  chat: { icon: 'globe', label: 'Open chat transcript' },
  rcm_hil: { icon: 'doc', label: 'Open in RCM worklist' },
  recall_call_task: { icon: 'refresh', label: 'Open recall' },
  triage_call_task: { icon: 'arrow-r', label: 'Open originating triage item' },
  kiosk: { icon: 'clipboard', label: 'Open kiosk submission' },
  kiosk_insurance: { icon: 'clipboard', label: 'Open kiosk submission' },
};

export const TI_SENTIMENT: Record<string, TriageSentimentConfig> = {
  Anxious: { color: 'var(--gold)', dot: 'var(--gold)' },
  Frustrated: { color: 'var(--terracotta)', dot: 'var(--terracotta)' },
  Negative: { color: 'var(--terracotta)', dot: 'var(--terracotta)' },
  Neutral: { color: 'var(--ink-2)', dot: 'var(--ink-3)' },
  Positive: { color: 'var(--success)', dot: 'var(--success)' },
};

export const TI_GUIDANCE_SOURCES: Record<string, TriageGuidanceSourceConfig> = {
  organization: {
    label: 'Your practice',
    color: 'var(--ai-3)',
    bg: 'var(--ai-tint)',
  },
  location: {
    label: 'Your practice',
    color: 'color-mix(in srgb, var(--success) 80%, #000)',
    bg: 'var(--success-tint)',
  },
  elva_wide: {
    label: 'ELVA best practice',
    color: 'color-mix(in srgb, var(--gold) 78%, #000)',
    bg: 'color-mix(in srgb, var(--gold) 18%, var(--cream-1))',
  },
  llm_general: {
    label: 'General guidance',
    color: 'var(--ink-2)',
    bg: 'var(--surface-mute)',
  },
};

export const TI_STATUS_KEYS = ['open', 'waiting', 'closed'] as const;

export interface TriageActionTypeConfig {
  verb: string;
  icon: IconKind;
  isComm?: boolean;
  color: string;
}

export const TI_ACTION_TYPES: Record<string, TriageActionTypeConfig> = {
  send_sms: {
    verb: 'Send SMS',
    icon: 'chat',
    isComm: true,
    color: 'color-mix(in srgb, var(--gold) 78%, #000)',
  },
  send_email: {
    verb: 'Send email',
    icon: 'mail',
    isComm: true,
    color: 'var(--ink-1)',
  },
  book_appointment: { verb: 'Book', icon: 'clock', color: 'var(--success)' },
  create_call_task: {
    verb: 'Add to Calls to make',
    icon: 'phone',
    color: 'color-mix(in srgb, var(--success) 80%, #000)',
  },
  escalate: { verb: 'Escalate', icon: 'team', color: 'var(--ai-3)' },
  notify_provider: { verb: 'Notify', icon: 'alert', color: 'var(--gold)' },
  add_note: {
    verb: 'Add note',
    icon: 'doc',
    color: 'color-mix(in srgb, var(--gold) 78%, #000)',
  },
  flag_internal: { verb: 'Flag internally', icon: 'alert', color: 'var(--ink-2)' },
  resume_recall: { verb: 'Schedule', icon: 'refresh', color: 'var(--ai-3)' },
};
